import { FC, useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import MainLayout from 'src/pages/private';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { useFormik } from 'formik';
import { customerInitialValues } from 'src/utils/initialValues.utils';
import { customerValidationSchema } from 'src/utils/validationScheme.utils';
import { apiUrls } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import SearchBar from 'src/components/InputFields/Searchbar';
import CustomerGrid from 'src/components/Grids/CustomerGrid';
import CustomDialog from 'src/components/DialogBox/FormBox/CustomBox';
import TextField from 'src/components/InputFields/TextField';
import MobileField from 'src/components/InputFields/MobileField';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { calculateDialogHeight } from 'src/utils/dialog.utils';
import {
  PrimaryActionButton,
  ButtonIcon,
} from 'src/components/StyledComponents/StyledPageComponents';

const CustomerManagement: FC = () => {
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [customerAdded, setCustomerAdded] = useState(false);
  const { showSnackbar } = StyledSnackbar();
  const [searchTerm, setSearchTerm] = useState('');

  const formik = useFormik({
    initialValues: customerInitialValues,
    validationSchema: customerValidationSchema,
    onSubmit: (values) => handleSubmit(values),
  });

  const getDialogHeight = () => {
    const fieldGroups = [
      ['name', 'customerCode'],
      ['organizationName', 'telephoneNo'],
      ['email'],
    ];
    return calculateDialogHeight(formik, fieldGroups);
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const addCustomer = await axiosTokenInstance.post(apiUrls.ADD_CUSTOMER, {
        name: values.name,
        customerCode: values.customerCode,
        organizationName: values.organizationName,
        telephoneNo: `+${values.countryCode} ${values.telephoneNo}`,
        email: values.email,
      });
      showSnackbar(addCustomer.data.message, 'success');
      setCustomerAdded(!customerAdded);
      handleClose();
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (newQuery: string) => {
    setSearchTerm(newQuery);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  const SearchBarComponent = () => (
    <SearchBar
      id="search"
      value={searchTerm}
      onChange={handleSearchChange}
      placeholder="Search by name, code, organization, email"
    />
  );

  const PrimaryActionButtonComponent = () => (
    <PrimaryActionButton
      className="btn-primary"
      width="180px"
      onClick={handleClickOpen}
    >
      <ButtonIcon component="i" className="bx bx-plus 1" />
      New Customer
    </PrimaryActionButton>
  );

  return (
    <MainLayout
      title="Customer Management"
      searchBar={<SearchBarComponent />}
      button={<PrimaryActionButtonComponent />}
    >
      <CustomerGrid state={customerAdded} searchTerm={searchTerm} />
      <Box display="flex">
        <Box display="flex" flexDirection="column">
          <CustomDialog
            height={`${getDialogHeight()}px`}
            open={open}
            handleClose={handleClose}
            title="Add New Customer"
            subtitle="Fill in the details below to add a new customer."
            onSubmit={formik.handleSubmit}
            smScreen={smScreen}
            buttonDisabled={loading}
          >
            <Box display="flex" flexDirection="column">
              <Box display="flex" justifyContent="space-between" gap={2} mt={2}>
                <TextField
                  width="300px"
                  id="name"
                  label="Name"
                  formikProps={formik}
                />

                <TextField
                  width="300px"
                  id="customerCode"
                  label="Code"
                  formikProps={formik}
                />
              </Box>

              <Box display="flex" justifyContent="space-between" gap={2}>
                <TextField
                  width="300px"
                  id="organizationName"
                  label="Organization"
                  formikProps={formik}
                />

                <MobileField
                  width="300px"
                  id="telephoneNo"
                  label="Telephone Number"
                  formikProps={formik}
                  widthPercent="35%"
                  fieldName="telephoneNo"
                />
              </Box>

              <Box display="flex" justifyContent="space-between" gap={2}>
                <TextField
                  width="300px"
                  id="email"
                  label="Email"
                  formikProps={formik}
                />
              </Box>
            </Box>
          </CustomDialog>
        </Box>
      </Box>
    </MainLayout>
  );
};

export default CustomerManagement;
